import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../components/PageHeader';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import { deliveryRadius, stores } from '../../services/apiClient';

function normalizeKey(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

const UF_CODES = new Set([
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
]);

function parseCityRegion(address) {
  const raw = String(address || '').trim();
  if (!raw) return { city: '', region: '' };

  const parts = raw.split(/[,\|]/).map((part) => part.trim()).filter(Boolean);
  if (!parts.length) return { city: '', region: '' };

  let city = '';
  let region = '';
  let regionIndex = -1;

  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const match = parts[i].match(/(.+?)\s*[-/]\s*([A-Za-z]{2})(?:\b|[^A-Za-z].*)$/);
    if (!match) continue;
    const uf = String(match[2] || '').toUpperCase();
    if (UF_CODES.has(uf)) {
      city = match[1].trim();
      region = uf;
      regionIndex = i;
      break;
    }
  }

  if (!region) {
    for (let i = parts.length - 1; i >= 0; i -= 1) {
      const match = parts[i].match(/\b([A-Za-z]{2})\b/);
      if (!match) continue;
      const uf = String(match[1] || '').toUpperCase();
      if (UF_CODES.has(uf)) {
        region = uf;
        regionIndex = i;
        break;
      }
    }
  }

  if (!city) {
    if (regionIndex > 0) {
      city = parts[regionIndex - 1];
    } else {
      city = parts.length >= 2 ? parts[parts.length - 2] : parts[parts.length - 1];
    }
  }

  return { city, region };
}

function clampKm(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return NaN;
  if (parsed < 0.1) return 0.1;
  return Number(parsed.toFixed(3));
}

function clampCourierKm(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return NaN;
  if (parsed < 0.1) return 0.1;
  if (parsed > 50) return 50;
  return Number(parsed.toFixed(3));
}

function metersToKm(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 10;
  return Number((parsed / 1000).toFixed(3));
}

function kmToMeters(value) {
  return Math.round(Number(value) * 1000);
}

export default function AdminRadius() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [storesData, setStoresData] = useState([]);
  const [radiusData, setRadiusData] = useState({
    global_order_radius_km: 7,
    courier_accept_radius_km: 10,
    courier_assign_radius_km: 10,
    rules: []
  });

  const [scopeType, setScopeType] = useState('city');
  const [scopeCity, setScopeCity] = useState('');
  const [scopeRegion, setScopeRegion] = useState('');
  const [scopeStoreId, setScopeStoreId] = useState('');
  const [scopeRadiusKm, setScopeRadiusKm] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [radiusResponse, storesResponse] = await Promise.all([
        deliveryRadius.get(),
        stores.list({})
      ]);

      setRadiusData({
        global_order_radius_km: Number(radiusResponse?.global_order_radius_km ?? 7),
        courier_accept_radius_km: metersToKm(radiusResponse?.courier_accept_radius_m ?? 10000),
        courier_assign_radius_km: metersToKm(radiusResponse?.courier_assign_radius_m ?? 10000),
        rules: Array.isArray(radiusResponse?.rules) ? radiusResponse.rules : []
      });
      setStoresData(Array.isArray(storesResponse) ? storesResponse : []);
    } catch {
      setError('Erro ao carregar configuracoes de raio.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const normalizedStores = useMemo(
    () => (Array.isArray(storesData) ? storesData : []).map((store) => {
      const parsed = parseCityRegion(store.address);
      const city = String(store.city || parsed.city || '').trim();
      const region = String(store.region || parsed.region || '').trim().toUpperCase();
      return {
        ...store,
        city,
        region,
        cityKey: normalizeKey(city),
        regionKey: normalizeKey(region)
      };
    }),
    [storesData]
  );

  const cities = useMemo(
    () => [...new Set(normalizedStores.map((store) => store.city).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [normalizedStores]
  );

  const regions = useMemo(
    () => [...new Set(normalizedStores.map((store) => store.region).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [normalizedStores]
  );

  const storesOptions = useMemo(
    () => [...normalizedStores].sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    [normalizedStores]
  );

  const ruleRows = useMemo(() => {
    const source = Array.isArray(radiusData.rules) ? radiusData.rules : [];
    return [...source].sort((a, b) => {
      const scopeA = String(a?.scope_type || '');
      const scopeB = String(b?.scope_type || '');
      if (scopeA !== scopeB) return scopeA.localeCompare(scopeB);
      return String(a?.scope_id || '').localeCompare(String(b?.scope_id || ''));
    });
  }, [radiusData.rules]);

  function handleAddOrUpdateRule() {
    let scopeId = '';
    if (scopeType === 'city') scopeId = scopeCity;
    if (scopeType === 'region') scopeId = scopeRegion;
    if (scopeType === 'store') scopeId = scopeStoreId;

    if (!scopeId) {
      alert('Selecione o alvo da regra (loja/cidade/regiao).');
      return;
    }

    const radiusKm = clampKm(scopeRadiusKm);
    if (!Number.isFinite(radiusKm)) {
      alert('Informe um raio valido em km.');
      return;
    }

    setRadiusData((prev) => {
      const rules = Array.isArray(prev.rules) ? [...prev.rules] : [];
      const idx = rules.findIndex(
        (rule) => String(rule?.scope_type || '').toLowerCase() === String(scopeType || '').toLowerCase()
          && String(rule?.scope_id || '') === String(scopeId)
      );
      const nextRule = {
        scope_type: scopeType,
        scope_id: scopeId,
        radius_km: radiusKm,
        is_active: true
      };
      if (idx >= 0) rules[idx] = nextRule;
      else rules.push(nextRule);
      return { ...prev, rules };
    });

    setScopeRadiusKm('');
  }

  function handleRemoveRule(ruleToRemove) {
    setRadiusData((prev) => ({
      ...prev,
      rules: (Array.isArray(prev.rules) ? prev.rules : []).filter(
        (rule) => !(
          String(rule?.scope_type || '').toLowerCase() === String(ruleToRemove?.scope_type || '').toLowerCase()
          && String(rule?.scope_id || '') === String(ruleToRemove?.scope_id || '')
        )
      )
    }));
  }

  async function handleSave() {
    const globalOrderRadiusKm = clampKm(radiusData.global_order_radius_km);
    const courierAcceptRadiusKm = clampCourierKm(radiusData.courier_accept_radius_km);
    const courierAssignRadiusKm = clampCourierKm(radiusData.courier_assign_radius_km);

    if (!Number.isFinite(globalOrderRadiusKm)) {
      alert('Defina um raio global de pedidos valido.');
      return;
    }
    if (!Number.isFinite(courierAcceptRadiusKm) || !Number.isFinite(courierAssignRadiusKm)) {
      alert('Defina os raios de motoboy em km com valores validos.');
      return;
    }

    const payloadRules = (Array.isArray(radiusData.rules) ? radiusData.rules : [])
      .map((rule) => ({
        scope_type: String(rule?.scope_type || '').toLowerCase(),
        scope_id: String(rule?.scope_id || '').trim(),
        radius_km: clampKm(rule?.radius_km),
        is_active: rule?.is_active !== false
      }))
      .filter((rule) => (
        rule.scope_id
        && ['store', 'city', 'region'].includes(rule.scope_type)
        && Number.isFinite(rule.radius_km)
      ));

    setSaving(true);
    try {
      await deliveryRadius.update({
        global_order_radius_km: globalOrderRadiusKm,
        courier_accept_radius_m: kmToMeters(courierAcceptRadiusKm),
        courier_assign_radius_m: kmToMeters(courierAssignRadiusKm),
        rules: payloadRules
      });
      await loadData();
      alert('Configuracoes de raio atualizadas com sucesso.');
    } catch (err) {
      alert(err?.response?.data?.detail || 'Nao foi possivel salvar as configuracoes de raio.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="admin-section">
      <PageHeader
        title="Raio"
        subtitle="Configure raio de pedidos das lojas e raio de aceitacao/atribuicao do motoboy"
        actions={(
          <button className="admin-button" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar alteracoes'}
          </button>
        )}
      />

      <div className="admin-card">
        <h3>Raio global</h3>
        <p style={{ color: '#B8B8C5', marginBottom: 12 }}>
          Este raio limita o checkout do cliente. Se a distancia loja-cliente passar do limite, o pedido e bloqueado.
        </p>
        <div className="admin-filters">
          <label style={{ display: 'grid', gap: 6, minWidth: 240 }}>
            <span style={{ color: '#B8B8C5', fontSize: 12 }}>Raio global de pedidos (km)</span>
            <input
              className="admin-input"
              type="number"
              min="0.1"
              step="0.1"
              value={radiusData.global_order_radius_km}
              onChange={(e) => setRadiusData((prev) => ({ ...prev, global_order_radius_km: e.target.value }))}
            />
          </label>
          <label style={{ display: 'grid', gap: 6, minWidth: 240 }}>
            <span style={{ color: '#B8B8C5', fontSize: 12 }}>Raio de aceitacao do motoboy (km)</span>
            <input
              className="admin-input"
              type="number"
              min="0.1"
              max="50"
              step="0.1"
              value={radiusData.courier_accept_radius_km}
              onChange={(e) => setRadiusData((prev) => ({ ...prev, courier_accept_radius_km: e.target.value }))}
            />
          </label>
          <label style={{ display: 'grid', gap: 6, minWidth: 240 }}>
            <span style={{ color: '#B8B8C5', fontSize: 12 }}>Raio de atribuicao do lojista (km)</span>
            <input
              className="admin-input"
              type="number"
              min="0.1"
              max="50"
              step="0.1"
              value={radiusData.courier_assign_radius_km}
              onChange={(e) => setRadiusData((prev) => ({ ...prev, courier_assign_radius_km: e.target.value }))}
            />
          </label>
        </div>
      </div>

      <div className="admin-card">
        <h3>Regra de raio por loja/cidade/regiao</h3>
        <p style={{ color: '#B8B8C5', marginBottom: 12 }}>
          Prioridade de aplicacao: Loja &gt; Cidade &gt; Regiao &gt; Global.
        </p>
        <div className="admin-filters">
          <select className="admin-select" value={scopeType} onChange={(e) => setScopeType(e.target.value)}>
            <option value="store">Por loja</option>
            <option value="city">Por cidade</option>
            <option value="region">Por regiao/UF</option>
          </select>

          <select
            className="admin-select"
            value={scopeStoreId}
            onChange={(e) => setScopeStoreId(e.target.value)}
            disabled={scopeType !== 'store'}
          >
            <option value="">Selecione a loja</option>
            {storesOptions.map((store) => (
              <option key={`store-radius-${store.id}`} value={store.id}>{store.name}</option>
            ))}
          </select>

          <select
            className="admin-select"
            value={scopeCity}
            onChange={(e) => setScopeCity(e.target.value)}
            disabled={scopeType !== 'city'}
          >
            <option value="">Selecione a cidade</option>
            {cities.map((city) => (
              <option key={`city-radius-${city}`} value={city}>{city}</option>
            ))}
          </select>

          <select
            className="admin-select"
            value={scopeRegion}
            onChange={(e) => setScopeRegion(e.target.value)}
            disabled={scopeType !== 'region'}
          >
            <option value="">Selecione a regiao/UF</option>
            {regions.map((region) => (
              <option key={`region-radius-${region}`} value={region}>{region}</option>
            ))}
          </select>

          <input
            className="admin-input"
            type="number"
            min="0.1"
            step="0.1"
            placeholder="Raio (km)"
            value={scopeRadiusKm}
            onChange={(e) => setScopeRadiusKm(e.target.value)}
          />

          <button className="admin-button secondary" type="button" onClick={handleAddOrUpdateRule}>
            Aplicar regra
          </button>
        </div>
      </div>

      <div className="admin-card">
        <h3>Regras ativas de raio</h3>
        <div className="admin-fees-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Escopo</th>
                <th>Alvo</th>
                <th>Raio (km)</th>
                <th>Status</th>
                <th>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {ruleRows.map((rule) => (
                <tr key={`radius-rule-${rule.scope_type}-${rule.scope_id}`}>
                  <td>{rule.scope_type === 'store' ? 'Loja' : rule.scope_type === 'city' ? 'Cidade' : 'Regiao'}</td>
                  <td>{rule.scope_id}</td>
                  <td>{Number(rule.radius_km || 0).toFixed(2)}</td>
                  <td>
                    <span className={`admin-badge ${rule.is_active === false ? 'neutral' : 'success'}`}>
                      {rule.is_active === false ? 'Inativa' : 'Ativa'}
                    </span>
                  </td>
                  <td>
                    <button
                      className="admin-button secondary"
                      type="button"
                      onClick={() => handleRemoveRule(rule)}
                    >
                      Remover
                    </button>
                  </td>
                </tr>
              ))}
              {!ruleRows.length && (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', color: '#B8B8C5' }}>
                    Nenhuma regra de raio ativa.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
